<template>
  <div :class="`base-table ${headerHidden ? 'header-hidden' : ''}`">
    <b-table
      striped
      hoverable
      :data="data"
      :loading="loading"
      :default-sort="sortField"
      :default-sort-direction="sortDirection"
      @sort="onSort"
      :focusable="selectable"
      :selected="selected"
      @select="onSelect"
      :checkable="checkable"
      :checked-rows="checked"
      :is-row-checkable="isRowCheckable"
      :custom-is-checked="customIsChecked"
      @check="onCheck"
      @check-all="(checked) => $emit('check-all', checked)"
      @click="onClick"
      :row-class="getRowClass"
      :detailed="detailed"
      :sticky-header="sticky"
      :height="finalHeight"
      :card-layout="cardLayout || isMobile"
      :mobile-cards="false"
      scrollable
      :backend-sorting="!uiPagination"
      :paginated="uiPagination && data.length > pagination.perPage"
      :current-page.sync="pagination.currentPage"
      :per-page="pagination.perPage"
      ref="btable"
    >
      <b-input
        v-model="props.filters[props.column.field]"
        slot="searchable"
        slot-scope="props"
        :placeholder="`Search ${props.column.label}`"
        v-if="!props.column.numeric"
      />
      <template slot-scope="props" slot="header">
        <slot
          :row="props.column"
          :name="props.column.meta.headerSlot"
          v-if="props.column.meta && props.column.meta.headerSlot"
        ></slot>
        <span v-else>{{ props.column.label }}</span>
      </template>
      <template slot-scope="props">
        <b-table-column
          v-for="(column, index) in finalColumns"
          :key="index"
          :custom-key="index"
          :field="column.field"
          :label="column.label"
          :sortable="column.sortable"
          :searchable="column.searchable"
          :centered="column.centered"
          :numeric="column.numeric"
          :meta="column.meta"
          class="no-wrap is-clipped ellipsis"
          :class="column.meta && column.meta.class ? column.meta.class : ''"
          :style="
            `${
              column.meta && column.meta.expanded
                ? 'width: 100%; max-width: 0'
                : ''
            }`
          "
        >
          <slot
            :name="column.meta.rowSlot"
            :row="props.row"
            :row-index="props.index"
            :column-index="index"
            v-if="column.meta && column.meta.rowSlot"
          ></slot>
          <span v-else>
            {{
              column.meta && column.meta.filter
                ? dynamicFilter(
                    column.meta.filter,
                    getObjectValue(
                      props.row,
                      column.meta && column.meta.valuePath
                        ? column.meta.valuePath
                        : column.field
                    ),
                    column.meta.filterOptions,
                  )
                : getObjectValue(
                    props.row,
                    column.meta && column.meta.valuePath
                      ? column.meta.valuePath
                      : column.field
                  )
            }}
          </span>
        </b-table-column>
      </template>
      <template slot="empty">
        <div class="content has-text-grey has-text-centered">
          <p>NO RECORDS FOUND</p>
        </div>
      </template>
      <template slot="detail" slot-scope="props" v-if="detailed">
        <slot name="detail" :row="props.row" />
      </template>
    </b-table>
    <div
      class="base-table--pagination"
      v-if="!uiPagination && !infiniteScroll && pagination.total > pagination.perPage"
    >
      <br />
      <b-pagination
        :total="pagination.total"
        :current.sync="pagination.currentPage"
        :per-page="pagination.perPage"
        aria-next-label="NEXT PAGE"
        aria-previous-label="LAST PAGE"
        aria-page-label="PAGE"
        aria-current-label="THIS PAGE"
        @change="onPageChange"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer.js";

export default {
  props: {
    service: {
      // required: true
    },
    fetchFunc: {
      type: String,
      // required: true
    },
    fetchParams: {
      type: Object,
      default: () => {}
    },
    /**
     * Custom fetch method
     * Has more priority over service
     */
    customFetch: {},
    columns: {
      type: Array,
      default: () => []
    },
    defaultSortField: {
      type: String,
      default: "id"
    },
    defaultSortDirection: {
      type: String,
      default: "desc"
    },
    perPage: {
      type: Number,
      default: 100
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Object,
      default: () => {}
    },
    checkable: {
      type: Boolean,
      default: false
    },
    checked: {
      type: Array,
      default: () => []
    },
    isRowCheckable: {
      type: Function,
      default: () => true
    },
    customIsChecked: {},
    sticky: {
      type: Boolean,
      default: false
    },
    height: {
      type: Number
    },
    infiniteScroll: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: true
    },
    narrowed: {
      type: Boolean,
      default: true
    },
    headerHidden: {
      type: Boolean,
      default: false
    },
    prepend: {
      type: Array,
      default: () => []
    },
    detailed: {
      type: Boolean,
      default: false
    },
    cardLayout: {
      type: Boolean,
      default: false
    },
    uiPagination: {
      type: Boolean,
      default: false,
    },
  },
  filters: {
    formattedDate(value, options = {}) {
      if (value == "N/A") return "";
      options = {
        format: "DD-MMM-YY",
        ...options,
      };
      return moment(value)
        .format(options.format)
        .toUpperCase();
    },
    formattedDateTime(value, options = {}) {
      if (value == "N/A") return "";
      options = {
        format: "DD-MMM-YY HH:mm",
        ...options,
      };
      return moment(value)
        .format(options.format)
        .toUpperCase();
    },
    formattedStatus(value) {
      return value.substring(0, 4);
    }
  },
  data: () => ({
    data: [],
    sortField: "",
    sortDirection: "",
    loading: false,
    pagination: {
      perPage: 100,
      total: 0,
      currentPage: 1
    },
    scrollableElement: null,
    page: 1,
    isMobile: false,
  }),
  computed: {
    finalColumns() {
      return this.columns.filter(column => {
        return column.meta ? !column.meta.hidden : true;
      });
    },
    finalHeight() {
      let less = 0;

      if (!this.infiniteScroll) {
        less += 48; // pagination height
      }

      return this.height - less;
    },
    queryParams() {
      return {
        order_by: this.sortField,
        sort_by: this.sortDirection,
        // per_page: this.pagination.perPage,
        per_page: this.uiPagination ? 100000 : this.pagination.perPage,
        page: this.page,
        ...this.fetchParams
      };
    }
  },
  created() {
    this.sortField = this.defaultSortField;
    this.sortDirection = this.defaultSortDirection;
    this.pagination.perPage = this.perPage;
  },
  mounted() {
    if (this.infiniteScroll) {
      this.scrollableElement = document.querySelector(
        ".base-table .table-wrapper"
      );

      this.$nextTick(() => {
        if (this.scrollableElement) {
          this.scrollableElement.addEventListener("scroll", this.onScroll);
        }
      });
    }

    // mobile-cards prop disables scroll, use card-layout prop instead
    if (window.matchMedia("(max-width: 768px)").matches) {
      this.isMobile = true;
    }
  },
  watch: {
    perPage(value) {
      this.pagination.perPage = value;
    },
    pagination: {
      handler: function(value) {
        this.$emit("paginate", value);
        // this.$emit("update:pagination", value);
      },
      deep: true
    },
    queryParams: {
      handler: function(value) {
        this.$emit("update:queryParams", value);
      },
      deep: true
    }
  },
  methods: {
    dynamicFilter(name, value, options) {
      options = options || {};
      return this.$options.filters[name](value, options);
    },
    getObjectValue(obj, path) {
      return path.split(".").reduce((acc, val) => {
        acc = acc[val] || "N/A";
        return acc;
      }, obj);
    },
    getRowClass(row, index) {
      return this.selected && this.selected.id === row.id ? "is-selected" : "";
    },
    prependData() {
      this.prepend.forEach(prepend => {
        this.data.unshift(prepend);
      });
    },
    fetchData(page = 1, append = false) {
      this.page = page;

      this.loading = true;

      this.$store.commit("_SET_ORDER_TABLE_PARAMS", {
        order_by: this.sortField,
        sort_by: this.sortDirection
      });

      const fetch = this.customFetch
        ? this.customFetch(this.queryParams)
        : this.service[this.fetchFunc](this.queryParams);

      return fetch.then(response => {
        const responseData = JsonDenormalizer.denormalize(response);

        if (append) {
          this.data = this.data.concat(responseData);
        } else {
          this.onSelect(null);
          this.onCheck([]);

          this.data = responseData;

          if (this.scrollable) {
            this.scrollable.scrollTop = 0;
          }
        }

        this.prependData();

        if (response.meta && response.meta.pagination) {
          this.pagination.perPage = this.uiPagination ? this.pagination.perPage : response.meta.pagination.per_page;
          this.pagination.total = response.meta.pagination.total;
          this.pagination.currentPage = this.uiPagination ? this.pagination.currentPage : response.meta.pagination.current_page;
          this.pagination.totalPages = response.meta.pagination.total_pages;
        }

        this.loading = false;

        this.$emit("fetch", this.data);
        return this.data;
      });
    },
    onPageChange(page) {
      if (!this.uiPagination) {
        this.fetchData(page);
      }
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortDirection = order;
      if (!this.uiPagination) {
        this.fetchData();
      }
    },
    onSelect(selected) {
      if (this.selectable) {
        this.$emit("select", selected);
        this.$emit("update:selected", selected);
      }
    },
    onCheck(checked, row) {
      this.$emit("check", checked, row);
      this.$emit("update:checked", checked);
    },
    onScroll() {
      const scroll = this.scrollableElement.scrollHeight;
      const scrolled = this.scrollableElement.scrollTop;
      const client = this.scrollableElement.clientHeight;

      if (
        scrolled + client === scroll &&
        this.pagination.currentPage < this.pagination.totalPages
      ) {
        this.fetchData(this.pagination.currentPage + 1, true);
      }
    },
    onClick(row) {
      this.$emit("click", row);
    }
  }
};
</script>

<style lang="scss">
.base-table {
  .table {
    /* table-layout: fixed; */
    width: 100%;
    td {
      text-overflow: ellipsis;
    }
    /**
    * Removes the outline when focused
    * Border is set to none (no focused) because it causes unnecessary animations?
    **/
    border: none;
    &:focus {
      border: none;
      box-shadow: none;
      outline: none;
    }
  }
  .no-wrap {
    white-space: nowrap;
  }
}

.base-table.sticky {
  th {
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }
}

.base-table.header-hidden {
  thead {
    tr:first-child {
      th {
        border: none;
        display: none;
      }
    }
  }
}

.base-table {
  thead {
    th {
      white-space: nowrap;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/partials/BaseTable.vue